/**
 * Mantrana — DOMAIN-SPECIFIC EMOTIONAL QUESTION POOLS (English).
 *
 * 20 questions per life-domain, organized into 5 buckets of 4 questions each,
 * one bucket per psychological dimension. Random sampling picks one question
 * from each bucket per session, so every visit surfaces a fresh but
 * dimension-complete 5-question profile.
 *
 * Every option is tagged with an archetype index (0..3) that maps to a
 * (label, score) pair via DIM_META below. Downstream aggregation stays
 * language-agnostic and deterministic.
 */

/* ----------------------------------------------------------------- *
 * Archetype metadata per dimension: [label, score].
 * The 4 archetypes per dimension carry the (label, score) that will
 * be recorded when a user picks that option. Option order per
 * question is FREE — each option only needs to carry its archetype
 * index (0..3) via the tuple [text, archetypeIndex].
 * ----------------------------------------------------------------- */
export const DIM_META = {
  decision_confidence: [
    ["analytical",   65],  // 0 — thinks, weighs
    ["intuitive",    85],  // 1 — knows quickly
    ["consultative", 45],  // 2 — asks trusted others
    ["delayed",      25],  // 3 — waits, avoids
  ],
  emotional_clarity: [
    ["self-aware",     60],  // 0 — names own feeling honestly
    ["externalizing",  35],  // 1 — blames outside forces
    ["withdrawn",      40],  // 2 — shuts down, goes silent
    ["grounded",       75],  // 3 — feels & acts steadily
  ],
  risk_readiness: [
    ["safety-first",       30],  // 0 — stability > opportunity
    ["growth-facing",      85],  // 1 — leans into discomfort
    ["uncertainty-averse", 25],  // 2 — avoids the unknown
    ["timing-led",         65],  // 3 — trusts right moment
  ],
  purpose_alignment: [
    ["security",    45],  // 0 — stability, safety
    ["recognition", 40],  // 1 — status, being seen
    ["freedom",     72],  // 2 — autonomy, space
    ["meaning",     92],  // 3 — depth, contribution
  ],
  self_trust: [
    ["fear-of-wrong-choice",   35],
    ["fear-of-judgment",       30],
    ["fear-of-losing-control", 45],
    ["fear-of-lost-potential", 72],
  ],
};

export const DIMENSIONS = Object.keys(DIM_META);

/* Legacy archetype names (used on old share cards / reports). */
export const DIM_TO_ARCHETYPE = {
  decision_confidence: "decision_style",
  emotional_clarity:   "emotional_pattern",
  risk_readiness:      "risk_orientation",
  purpose_alignment:   "core_motivation",
  self_trust:          "hidden_conflict",
};

/* ----------------------------------------------------------------- *
 * POOL_EN — every question shaped as:
 *   { q: "…", o: [ [text, archIdx], [text, archIdx], … 4 total ] }
 *
 * Options within a question are free-order — the archIdx (0..3) is
 * what carries the scoring, not the display order.
 * ----------------------------------------------------------------- */
export const POOL_EN = {
  Career: {
    decision_confidence: [
      { q: "When a real career decision arrives, what actually happens inside you first?",
        o: [
          ["I start weighing every angle in my head",                       0],
          ["Something in me already knows the answer before I think it",    1],
          ["I need to talk it through with someone I trust",                2],
          ["I put it off and hope clarity finds me later",                  3],
        ] },
      { q: "Which sentence feels closest to your career life right now?",
        o: [
          ["I know what I want but can't quite reach it",                   0],
          ["Something inside is pointing me toward a bigger version of me", 1],
          ["I'd know what to do if the right person told me it's okay",     2],
          ["I don't know what I want anymore",                              3],
        ] },
      { q: "When a good opportunity appears, what usually happens?",
        o: [
          ["I map out worst-case, best-case, and everything in between",    0],
          ["I say yes before my mind talks me out of it",                   1],
          ["I run it past a few people before deciding",                    2],
          ["I hesitate long enough that the moment passes",                 3],
        ] },
      { q: "Looking at your last big career choice, how did you actually make it?",
        o: [
          ["Deliberately — I researched, listed, compared",                 0],
          ["It felt right in my gut and I trusted it",                      1],
          ["A person I respect nudged me forward",                          2],
          ["It happened because I waited too long to choose",               3],
        ] },
    ],
    emotional_clarity: [
      { q: "What bothers you more about your current professional life?",
        o: [
          ["That I'm exhausted and I can't say why",                        0],
          ["That people around me keep holding me back",                    1],
          ["That I've quietly gone numb about it",                          2],
          ["That I know exactly what needs fixing and I'm doing it",        3],
        ] },
      { q: "When work doesn't go as you hoped, what tends to happen inside?",
        o: [
          ["I sit with it and try to understand what I felt",               0],
          ["I get frustrated with the system, the boss, the timing",        1],
          ["I go quiet and keep everything to myself",                      2],
          ["I roll up my sleeves and start fixing something",               3],
        ] },
      { q: "On the days work feels heaviest, what usually shows up?",
        o: [
          ["A soft heaviness I can actually name",                          0],
          ["Irritation at people who don't do their part",                  1],
          ["A slow retreat — I stop replying, stop caring",                 2],
          ["A push to do something, anything, to feel useful",              3],
        ] },
      { q: "After a hard week, how do you usually process it?",
        o: [
          ["I journal, walk, let the feeling arrive",                       0],
          ["I vent — the situation was unfair",                             1],
          ["I disappear into distractions and hope Monday feels different", 2],
          ["I plan the next week down to the hour",                         3],
        ] },
    ],
    risk_readiness: [
      { q: "What usually stops you from taking a professional leap?",
        o: [
          ["It could be reckless if I don't have a fallback",               0],
          ["Nothing much — I'll figure it out as I go",                     1],
          ["Uncertainty about the outcome — I can't picture it",            2],
          ["I'm waiting for the timing to actually be right",               3],
        ] },
      { q: "If a stable job and a risky dream both landed on your table tomorrow:",
        o: [
          ["I'd choose the stable one — peace of mind matters",             0],
          ["I'd choose the dream — this is the moment",                     1],
          ["I'd panic and probably delay both",                             2],
          ["I'd wait a season and see how each feels",                      3],
        ] },
      { q: "How do you relate to a career move that has no guarantees?",
        o: [
          ["I need at least a soft safety net first",                       0],
          ["Discomfort is where I actually grow",                           1],
          ["The not-knowing is what breaks me",                             2],
          ["I trust I'll know when the door is actually open",              3],
        ] },
      { q: "When your peers make bold career moves, what does that stir in you?",
        o: [
          ["Concern — are they being careful enough?",                      0],
          ["A pull — I've been sitting on my own move for too long",        1],
          ["Anxiety — what if I fall behind if I don't move too?",          2],
          ["Curiosity — I want to see how it unfolds first",                3],
        ] },
    ],
    purpose_alignment: [
      { q: "If money were solved forever, what would you still choose to do?",
        o: [
          ["Something steady that keeps my days in order",                  0],
          ["Something that makes me visible, respected, known",             1],
          ["Something free — no boss, no schedule, no cage",                2],
          ["Something that quietly matters — I'd want to build meaning",    3],
        ] },
      { q: "What actually pulls you toward your current line of work?",
        o: [
          ["It gives me stability and I can plan a life around it",         0],
          ["It lets me be seen and appreciated for what I do",              1],
          ["It gives me room to move on my own terms",                      2],
          ["It connects to something larger than a paycheck",               3],
        ] },
      { q: "When you imagine ten years from now, what do you most want?",
        o: [
          ["A settled, well-organized life I don't have to worry about",    0],
          ["A name people know and respect",                                1],
          ["Enough freedom that I own my time",                             2],
          ["A life that clearly meant something",                           3],
        ] },
      { q: "What does a genuinely good work-day look like to you?",
        o: [
          ["Everything I planned actually happened, on time",               0],
          ["Someone important noticed what I did",                          1],
          ["I moved through the day at my own pace",                        2],
          ["I felt like I helped something real move forward",              3],
        ] },
    ],
    self_trust: [
      { q: "What kind of professional criticism cuts you the deepest?",
        o: [
          ["Being told I'm not thinking clearly",                           0],
          ["Being called out publicly, in front of others",                 1],
          ["Being told the situation is out of my hands",                   2],
          ["Being told I'm not living up to what I could",                  3],
        ] },
      { q: "What's the quiet fear that shapes your career choices?",
        o: [
          ["That I'll make the wrong call and can't undo it",               0],
          ["That people will look at me and think less of me",              1],
          ["That I'll no longer be able to steer my own life",              2],
          ["That I'll die without ever having done the work I was meant to",3],
        ] },
      { q: "When you replay a career mistake in your head, what stings most?",
        o: [
          ["That I should have seen it coming",                             0],
          ["That other people saw me fail",                                 1],
          ["That the moment slipped out of my control",                     2],
          ["That I let a version of myself down",                           3],
        ] },
      { q: "Why do you sometimes hold back from what you truly want at work?",
        o: [
          ["Because I might be wrong about wanting it",                     0],
          ["Because people would judge the choice",                         1],
          ["Because it would blow up my current life",                      2],
          ["Because if I fail at this, I have nothing bigger to reach for", 3],
        ] },
    ],
  },

  Relationships: {
    decision_confidence: [
      { q: "When a relationship reaches a real turning point, how do you decide?",
        o: [
          ["I think it through — pros, cons, patterns, red flags",          0],
          ["I already know inside — my body tells me before my mind",       1],
          ["I ask two or three people who love me for their read",          2],
          ["I let time make the choice for me",                             3],
        ] },
      { q: "If you had to leave — or stay — in the next 30 days, what would happen?",
        o: [
          ["I'd list everything I've felt and try to see it clearly",       0],
          ["I already know — I've been avoiding the answer",                1],
          ["I'd want to hear it from someone I trust before I move",        2],
          ["I'd probably stall and hope the choice makes itself",           3],
        ] },
      { q: "When love gets confusing, what usually happens next?",
        o: [
          ["I try to understand the pattern between us",                    0],
          ["I follow the feeling — my instinct rarely lies to me",          1],
          ["I need to talk to a friend before I know what I feel",          2],
          ["I put the whole thing on the shelf",                            3],
        ] },
      { q: "How do you typically make decisions about who to trust?",
        o: [
          ["Slowly — I watch actions across time",                          0],
          ["Very quickly — I read people almost instantly",                 1],
          ["I check with the people who know me best",                      2],
          ["I don't decide — I stay guarded and let them prove it",         3],
        ] },
    ],
    emotional_clarity: [
      { q: "When someone you deeply care about becomes distant, what actually hurts you most?",
        o: [
          ["That I lose the sense of security I had",                       0],
          ["That they stopped sharing their world with me",                 1],
          ["That I don't know what I did wrong",                            2],
          ["That I start to feel easily replaceable",                       3],
        ] },
      { q: "When you're upset with someone close, what do you usually do first?",
        o: [
          ["I withdraw and try to understand what I'm feeling",             0],
          ["I confront them right away",                                    1],
          ["I pretend everything is okay until it isn't",                   2],
          ["I do something practical — write, walk, call a friend",         3],
        ] },
      { q: "When a fight ends, what tends to happen in you?",
        o: [
          ["I stay quiet with it — I need time to feel it fully",           0],
          ["I keep replaying what they did wrong",                          1],
          ["I go cold — I stop bringing warmth for a while",                2],
          ["I move toward repair — apology, gesture, action",               3],
        ] },
      { q: "How do you usually handle a partner or friend's silence?",
        o: [
          ["I try to name what I feel about it — hurt, fear, worry",        0],
          ["I get sharp — the silence is unfair",                           1],
          ["I match their silence and go further away myself",              2],
          ["I reach out first, kindly, and see what's underneath",          3],
        ] },
    ],
    risk_readiness: [
      { q: "When it comes to opening your heart, which feels most true?",
        o: [
          ["I want love, but only when it feels safe",                      0],
          ["I'd rather feel deeply and get hurt than never feel much",      1],
          ["I hold back — being wrong about someone terrifies me",          2],
          ["I open up when the moment feels right, not before",             3],
        ] },
      { q: "How do you relate to being vulnerable with someone new?",
        o: [
          ["I share slowly, only what feels okay to lose",                  0],
          ["I go in fully — safety comes from honesty, not caution",        1],
          ["I dread it — being seen scares me more than being alone",       2],
          ["I let it unfold — the right depth arrives at the right time",   3],
        ] },
      { q: "If your partner suggested a big life change together, your first instinct?",
        o: [
          ["I'd want a very clear plan before I said yes",                  0],
          ["Yes — let's stretch, let's grow, let's do it",                  1],
          ["I'd freeze — too many unknowns to hold at once",                2],
          ["I'd say: let's revisit in a season, if we still feel it",       3],
        ] },
      { q: "What kind of relationship risk feels hardest to take?",
        o: [
          ["The kind that could break the stability I've built",            0],
          ["The kind where I hold nothing back and might be rejected",      1],
          ["Any kind — uncertainty in love drains me the most",             2],
          ["Moving too fast when the timing hasn't arrived",                3],
        ] },
    ],
    purpose_alignment: [
      { q: "Deep down, what are you secretly hoping a relationship will give you?",
        o: [
          ["A sense of home — someone I can rest my whole life against",    0],
          ["To be truly seen and cherished, not just tolerated",            1],
          ["Room to still be my own person inside the love",                2],
          ["A shared meaning — that we built something real together",      3],
        ] },
      { q: "In your closest bonds, what do you actually crave most?",
        o: [
          ["Steadiness — I want to know they'll be there tomorrow",         0],
          ["To be admired, chosen, held up in someone's eyes",              1],
          ["Space, freedom, no pressure to perform",                        2],
          ["A partnership that means something beyond the two of us",       3],
        ] },
      { q: "Which line describes what love should feel like?",
        o: [
          ["A safe harbor",                                                 0],
          ["A stage where I'm loved out loud",                              1],
          ["A garden where I still grow on my own",                         2],
          ["A quiet promise that we're building meaning together",          3],
        ] },
      { q: "If you had one relationship left in this life, you'd want it to be…",
        o: [
          ["The one that keeps me safe when the world doesn't",             0],
          ["The one where I feel truly wanted, every day",                  1],
          ["The one where I never have to shrink to fit in",                2],
          ["The one that makes both our lives mean something",              3],
        ] },
    ],
    self_trust: [
      { q: "Which feeling scares you the most in love?",
        o: [
          ["Being abandoned",                                               0],
          ["Being misunderstood",                                           1],
          ["Being controlled",                                              2],
          ["Never finding the right person",                                3],
        ] },
      { q: "If you could relive one relationship conversation, what would you change?",
        o: [
          ["What I said — it came out wrong",                               0],
          ["What I didn't say — I stayed too small",                        1],
          ["The person I chose to say it to",                               2],
          ["Nothing — but the fact that I still replay it means something", 3],
        ] },
      { q: "What holds you back from asking for what you really want in love?",
        o: [
          ["I might be wrong about wanting it",                             0],
          ["They'll see me differently — needier, weaker",                  1],
          ["It might change the balance we've settled into",                2],
          ["Because if they say no, part of me will never try again",       3],
        ] },
      { q: "What's the quiet fear that shapes how you love?",
        o: [
          ["That I'll choose wrong and waste years",                        0],
          ["That they'll see the parts of me I hide",                       1],
          ["That love means giving up a piece of myself",                   2],
          ["That I'll die without loving the way I could have",             3],
        ] },
    ],
  },

  Money: {
    decision_confidence: [
      { q: "When a real financial decision lands on your desk, what happens first?",
        o: [
          ["I open a spreadsheet — no big money move without the numbers",  0],
          ["My gut already tells me yes or no",                             1],
          ["I ask someone who understands money better than I do",          2],
          ["I sit on it until the choice makes itself",                     3],
        ] },
      { q: "How did you make your last significant money decision?",
        o: [
          ["I researched, compared, and stress-tested it",                  0],
          ["It felt right — that was enough for me",                        1],
          ["Someone I trust helped me decide",                              2],
          ["Honestly, I let the deadline decide",                           3],
        ] },
      { q: "How do you usually approach big purchases or investments?",
        o: [
          ["Slowly, with math, over weeks",                                 0],
          ["Quickly, when the moment feels right",                          1],
          ["I check with an advisor or family member first",                2],
          ["I procrastinate and often miss the window",                     3],
        ] },
      { q: "If a lucrative but unfamiliar money opportunity showed up today:",
        o: [
          ["I'd study it for weeks before touching it",                     0],
          ["I'd move quickly if the shape of it felt right",                1],
          ["I'd forward it to someone smarter for their read",              2],
          ["I'd think about it and quietly let it pass",                    3],
        ] },
    ],
    emotional_clarity: [
      { q: "What emotion visits you most often when you think about money?",
        o: [
          ["A steady sense of responsibility — I know it's on me",          0],
          ["Frustration — I feel like I've been shortchanged",              1],
          ["Anxiety — I avoid thinking about it as much as I can",          2],
          ["A quiet excitement — I'm ready to build",                       3],
        ] },
      { q: "When money things go wrong, what usually happens in you?",
        o: [
          ["I sit with it and try to see what it's really about",           0],
          ["I blame the market, my job, or the timing",                     1],
          ["I go silent and stop checking the account for weeks",           2],
          ["I make a plan the same day and start fixing it",                3],
        ] },
      { q: "Which line most describes how money feels to you?",
        o: [
          ["A weight I carry consciously, but calmly",                      0],
          ["A frustrating thing that never quite adds up",                  1],
          ["A subject I quietly dread",                                     2],
          ["A tool — I'm learning to use it well",                          3],
        ] },
      { q: "When someone else brings up money, how do you feel?",
        o: [
          ["Curious — I want to understand my own relationship better",     0],
          ["Judged — I feel like I'm being measured",                       1],
          ["Uncomfortable — I change the subject",                          2],
          ["Grounded — I know where I stand and what I'm building",         3],
        ] },
    ],
    risk_readiness: [
      { q: "How much financial risk feels healthy to you right now?",
        o: [
          ["Almost none — I want everything protected",                     0],
          ["Meaningful risk — nothing grows in a safe box",                 1],
          ["I avoid risk because I can't stomach the not-knowing",          2],
          ["Timed risk — right amount, right season",                       3],
        ] },
      { q: "If you had extra money tomorrow, where would it go?",
        o: [
          ["Straight into savings and safety",                              0],
          ["Into something that could multiply, even if it might not",      1],
          ["Nowhere — I'd freeze and think about it for months",            2],
          ["Into an opportunity I've been watching for a while",            3],
        ] },
      { q: "How do you feel about starting something of your own with money?",
        o: [
          ["I'd need a very stable safety net first",                       0],
          ["I'd take the leap — this is how real wealth is built",          1],
          ["The uncertainty alone stops me from starting",                  2],
          ["I trust the right moment will make it obvious",                 3],
        ] },
      { q: "If someone offered you an aggressive investment with high upside:",
        o: [
          ["Not a chance — I need protection over potential",               0],
          ["I'd study it, and if the odds are honest, I'd move",            1],
          ["I'd say no — my nervous system can't handle it",                2],
          ["I'd wait for the market to tell me the moment",                 3],
        ] },
    ],
    purpose_alignment: [
      { q: "If you became financially free tomorrow, what would actually change first?",
        o: [
          ["My peace of mind — I could finally exhale",                     0],
          ["The way I show up — I'd finally be seen at my full size",       1],
          ["My schedule — I'd own my time completely",                      2],
          ["My work — I'd choose only things that matter",                  3],
        ] },
      { q: "What does 'enough' actually mean to you?",
        o: [
          ["Enough is safety — a life I don't have to worry about",         0],
          ["Enough is being known for what I've built",                     1],
          ["Enough is freedom — I answer to no one",                        2],
          ["Enough is when the money lines up with my meaning",             3],
        ] },
      { q: "Growing up, what belief about money shaped you most?",
        o: [
          ["Money is hard to earn — keep it safe when you have it",         0],
          ["Money brings respect — people notice you when you have it",     1],
          ["Money buys freedom — I never wanted to be owned by anything",   2],
          ["Money is a means — meaning is the point",                       3],
        ] },
      { q: "What are you really working toward with your money?",
        o: [
          ["A soft, secure life",                                           0],
          ["The kind of life people notice",                                1],
          ["The freedom to leave anything I no longer want",                2],
          ["A life whose numbers reflect who I actually am",                3],
        ] },
    ],
    self_trust: [
      { q: "What kind of money mistake do you regret most?",
        o: [
          ["Trusting the wrong person or the wrong information",            0],
          ["Being visible about a loss — others saw it too",                1],
          ["Losing my grip — the money moved without me",                   2],
          ["Not taking a risk that might have changed everything",          3],
        ] },
      { q: "If you lost most of your savings tomorrow, what would sting most?",
        o: [
          ["That I might have avoided it if I'd been more careful",         0],
          ["That people would see me differently now",                      1],
          ["That my independence would slip out of my hands",               2],
          ["That the future I was building for myself would disappear",     3],
        ] },
      { q: "What holds you back from making the money moves you want?",
        o: [
          ["I might make the wrong call — I don't fully trust my read",     0],
          ["People would judge me if it doesn't work",                      1],
          ["It could destabilize the life I've carefully built",            2],
          ["If I fail at this, I lose the future I was aiming for",         3],
        ] },
      { q: "Why does money still feel loaded for you?",
        o: [
          ["Because I don't fully trust my own decisions with it",          0],
          ["Because it's tied to how the world sees me",                    1],
          ["Because I've been out of control with it once before",          2],
          ["Because it decides whether I get to become who I could be",     3],
        ] },
    ],
  },

  Family: {
    decision_confidence: [
      { q: "When a hard family decision arrives, what usually happens in you?",
        o: [
          ["I think through what's fair for everyone before I speak",       0],
          ["I already know what needs to happen, deep down",                1],
          ["I ask a sibling or a close cousin before I decide",             2],
          ["I wait to see what my family decides first",                    3],
        ] },
      { q: "Who do you tend to trust most when family choices get complicated?",
        o: [
          ["My own head — I map it out carefully",                          0],
          ["My gut — my body tells me before my mind agrees",               1],
          ["A specific person in my family who knows the truth",            2],
          ["Nobody — I stall until circumstances force a move",             3],
        ] },
      { q: "How did you handle the last big family decision that involved you?",
        o: [
          ["I gathered facts, thought it through, then spoke",              0],
          ["I said what I felt was true, even when it was hard",            1],
          ["I mostly listened and went with the family consensus",          2],
          ["I stayed quiet and let it happen around me",                    3],
        ] },
      { q: "How do you make decisions when your family expects one thing but you feel another?",
        o: [
          ["I weigh both carefully — is my truth or their peace bigger?",   0],
          ["I follow the pull inside me, even if it hurts them briefly",    1],
          ["I lean on someone who knows both me and my family well",        2],
          ["I go with what they want — for now — and quietly ache",         3],
        ] },
    ],
    emotional_clarity: [
      { q: "What hurts you most in your family relationships?",
        o: [
          ["Feeling unseen, even when I'm doing so much",                   0],
          ["Constant criticism — nothing I do is quite right",              1],
          ["A slow emotional distance no one talks about",                  2],
          ["Being handed responsibilities I never actually agreed to",      3],
        ] },
      { q: "When family conflict happens, what do you usually do?",
        o: [
          ["I try to understand what everyone is actually afraid of",       0],
          ["I fight my corner — someone has to say the truth",              1],
          ["I stay silent and hope the storm passes",                       2],
          ["I step in and try to hold the pieces together",                 3],
        ] },
      { q: "How do you react when a family member wounds you?",
        o: [
          ["I sit with it and try to name why it hurt so much",             0],
          ["I get sharp — they knew what they were doing",                  1],
          ["I go cold — I distance without saying anything",                2],
          ["I focus on repair — I want the peace back sooner than the pain",3],
        ] },
      { q: "What do you carry from your childhood family that still shapes you?",
        o: [
          ["A knowing about what I felt — even when no one asked",          0],
          ["A quiet anger about what wasn't fair",                          1],
          ["A habit of shrinking so nothing gets loud",                     2],
          ["A capacity to hold everyone else together",                     3],
        ] },
    ],
    risk_readiness: [
      { q: "How do you feel about disappointing your family for something you truly want?",
        o: [
          ["I'd choose their peace — I need them steady",                   0],
          ["I'd disappoint them — my life is mine to live",                 1],
          ["Even thinking about it makes me freeze",                        2],
          ["I'd wait for the right season to bring it up",                  3],
        ] },
      { q: "If your family strongly opposed your dream, what would you do?",
        o: [
          ["I'd shelve it — family stability comes first",                  0],
          ["I'd pursue it and let them come around in time",                1],
          ["I'd stop pursuing it — the tension would be too heavy",         2],
          ["I'd take small, quiet steps until the timing softens",          3],
        ] },
      { q: "How do you feel about going against a family tradition or expectation?",
        o: [
          ["Uncomfortable — tradition is part of how I feel safe",          0],
          ["Ready — I owe my life more than I owe the pattern",             1],
          ["Terrified — I don't know who I'd be without the frame",         2],
          ["Cautious — I'd break it slowly, in the right window",           3],
        ] },
      { q: "How much of your life do you keep hidden from your family?",
        o: [
          ["Very little — safety comes from not shocking anyone",           0],
          ["A lot — I've chosen to live truthfully, not comfortably",       1],
          ["Almost everything real — I can't bear their reactions",         2],
          ["Some things, until the moment feels right to reveal them",      3],
        ] },
    ],
    purpose_alignment: [
      { q: "If you could hear one sentence from your family, which would you want most?",
        o: [
          ["'We're here. You don't have to carry it alone.'",               0],
          ["'We're proud of who you've become.'",                           1],
          ["'You don't owe us anything. Live your life.'",                  2],
          ["'You were right to become the person you are.'",                3],
        ] },
      { q: "What role do you find yourself playing in your family?",
        o: [
          ["The responsible one — the anchor",                              0],
          ["The one who's expected to make everyone proud",                 1],
          ["The one who quietly kept their independence",                   2],
          ["The one who's trying to become more than the family script",    3],
        ] },
      { q: "What do you actually want your family life to feel like?",
        o: [
          ["Steady, dependable, safe",                                      0],
          ["A place where I feel appreciated for who I am",                 1],
          ["A place I return to freely, without duty",                      2],
          ["A place where our lives together mean something",               3],
        ] },
      { q: "Which sentence describes what family should give a person?",
        o: [
          ["A soft place to fall",                                          0],
          ["The mirror that says: 'you matter here'",                       1],
          ["Room to breathe and grow apart",                                2],
          ["A shared story that means something across years",              3],
        ] },
    ],
    self_trust: [
      { q: "Which family expectation has shaped your life the most?",
        o: [
          ["Career expectations — the pressure to build the right life",    0],
          ["The expectation of how a good son/daughter should appear",      1],
          ["Emotional responsibilities I never got to opt out of",          2],
          ["The expectation that I would become more than they could",      3],
        ] },
      { q: "What quiet fear runs under your family choices?",
        o: [
          ["That I'll make a call that fractures the family",               0],
          ["That they'll see me and finally think less of me",              1],
          ["That I'll lose the grip on my own life if I let them in",       2],
          ["That I'll die without ever having become my own person",        3],
        ] },
      { q: "What holds you back from saying what you actually feel to family?",
        o: [
          ["I might be wrong, and I can't unsay it",                        0],
          ["I don't want to be seen as ungrateful or dramatic",             1],
          ["It would break the order we've all agreed on",                  2],
          ["If they can't handle my truth, we lose each other",             3],
        ] },
      { q: "What do you secretly wish you could stop being for your family?",
        o: [
          ["The one who always thinks it through",                          0],
          ["The one who always makes them look good",                       1],
          ["The one who holds everything together",                         2],
          ["The one who's forever becoming, never arrived",                 3],
        ] },
    ],
  },

  Purpose: {
    decision_confidence: [
      { q: "When a life-direction shift starts calling you, how do you respond?",
        o: [
          ["I sit down and map it out — meaning needs a plan",              0],
          ["I follow it — my inner compass rarely misleads me",             1],
          ["I discuss it with a mentor or friend I trust",                  2],
          ["I wait — maybe the pull will settle or grow clearer",           3],
        ] },
      { q: "How do you tend to make life-direction decisions?",
        o: [
          ["Slowly, methodically — my future is not to be rushed",          0],
          ["Quickly, once the answer arrives in my body",                   1],
          ["Through conversations that help me hear myself",                2],
          ["Not really — I let life keep choosing for me",                  3],
        ] },
      { q: "When your inner voice tells you your current life is wrong for you:",
        o: [
          ["I test the feeling — is this real or just a hard week?",        0],
          ["I trust it — that voice has never been wrong before",           1],
          ["I check with people who know me to see if they see it too",     2],
          ["I ignore it and hope it goes away",                             3],
        ] },
      { q: "How do you decide what to say yes and no to in life right now?",
        o: [
          ["Through careful reflection — what's this actually for?",        0],
          ["Through instinct — either the yes is real or it isn't",         1],
          ["By seeing what the people who love me say",                     2],
          ["By default — I usually agree and figure it out later",          3],
        ] },
    ],
    emotional_clarity: [
      { q: "Which thought visits you most often late at night?",
        o: [
          ["Why do I still feel incomplete after so much?",                 0],
          ["Am I wasting my life on things that aren't mine?",              1],
          ["Is this really all there is?",                                  2],
          ["What am I truly here to do?",                                   3],
        ] },
      { q: "When your path feels unclear, what usually happens inside you?",
        o: [
          ["I turn inward and try to name what I actually feel",            0],
          ["I get frustrated with the system, the world, the timing",       1],
          ["I go quiet — I stop dreaming out loud",                         2],
          ["I keep moving — I trust motion will show me something",         3],
        ] },
      { q: "What's the emotional weather of your life these days?",
        o: [
          ["A soft, honest sadness I've stopped running from",              0],
          ["A restless irritation I can't quite direct",                    1],
          ["A quiet numbness — I don't feel much anymore",                  2],
          ["An early morning stillness — something is turning",             3],
        ] },
      { q: "How do you know when your life is out of alignment?",
        o: [
          ["My body tells me before anyone else does",                      0],
          ["Everything around me starts irritating me",                     1],
          ["I lose interest in things I used to love",                      2],
          ["A new pull shows up and won't be denied",                       3],
        ] },
    ],
    risk_readiness: [
      { q: "How ready are you to change the shape of your whole life?",
        o: [
          ["Cautious — I want change without breaking what already works",  0],
          ["Ready — I've been waiting for permission",                      1],
          ["Not ready — the uncertainty terrifies me",                      2],
          ["Watchful — the right window will open",                         3],
        ] },
      { q: "If nobody could judge you, what would you actually pursue?",
        o: [
          ["Something steady that fits the life I've built",                0],
          ["A dream — the one I've been quiet about",                       1],
          ["Something spiritual, inward, that I've never spoken aloud",     2],
          ["Something adventurous — I'd risk more than I have",             3],
        ] },
      { q: "How does your body feel when you imagine changing everything?",
        o: [
          ["Tight — I want to protect what I know",                         0],
          ["Alive — that's the direction I've been avoiding",               1],
          ["Frozen — I can't picture the after",                            2],
          ["Steady — it's coming, just not today",                          3],
        ] },
      { q: "Which sentence about risk feels most true for the life you want?",
        o: [
          ["I'll grow, but only inside the walls I already trust",          0],
          ["Nothing meaningful happens without something breaking first",   1],
          ["I don't do risk — my system can't hold it",                     2],
          ["I take the leap when the ground actually feels ready",          3],
        ] },
    ],
    purpose_alignment: [
      { q: "What gives you the strongest feeling that you're alive?",
        o: [
          ["A day that unfolds exactly the way I intended",                 0],
          ["Being appreciated by people who matter to me",                  1],
          ["Complete freedom — nothing owed to anyone",                     2],
          ["Building or helping something that will outlast me",            3],
        ] },
      { q: "At this stage of your life, what are you searching for most?",
        o: [
          ["Peace",                                                         0],
          ["Success — I want to know I'm winning",                          1],
          ["Freedom — from expectations, from the past",                    2],
          ["Purpose — a reason bigger than survival",                       3],
        ] },
      { q: "What does 'a life well-lived' look like to you?",
        o: [
          ["Quiet, kind, undisturbed",                                      0],
          ["Loud, celebrated, remembered",                                  1],
          ["Free, self-owned, on my terms",                                 2],
          ["Meaningful — someone somewhere is better because of it",        3],
        ] },
      { q: "When you strip your ambitions to the bone, what's left?",
        o: [
          ["A wish to be safe and steady",                                  0],
          ["A wish to be seen and remembered",                              1],
          ["A wish to be free of all of it",                                2],
          ["A wish to matter to something larger than myself",              3],
        ] },
    ],
    self_trust: [
      { q: "What do you fear more?",
        o: [
          ["Failure",                                                       0],
          ["Being forgotten",                                               1],
          ["Meaninglessness",                                               2],
          ["Regret",                                                        3],
        ] },
      { q: "What's the fear you don't say out loud about your life?",
        o: [
          ["That I'll choose the wrong direction and it'll be too late",    0],
          ["That people will see me not living my own truth",               1],
          ["That I'll no longer recognize the life I've built",             2],
          ["That my one shot will pass without me really taking it",        3],
        ] },
      { q: "What holds you back from becoming who you sense you could be?",
        o: [
          ["I might be wrong about what I sense — and I can't undo the leap",0],
          ["People close to me won't understand",                           1],
          ["I'd lose the shape of the life I already know",                 2],
          ["That version of me might still not be enough",                  3],
        ] },
      { q: "What do you most want to prove to yourself before you die?",
        o: [
          ["That I lived kindly, no matter what",                           0],
          ["That I was more than what people saw of me",                    1],
          ["That I stayed my own person all the way through",               2],
          ["That I actually did the thing I was here to do",                3],
        ] },
    ],
  },

  "Major Decision": {
    decision_confidence: [
      { q: "When facing a real, weighty decision, how do you usually meet it?",
        o: [
          ["I go slow — I need to understand every dimension",              0],
          ["I go fast — I know once I know",                                1],
          ["I want the read of two or three people first",                  2],
          ["I sit on it and let the deadline pick for me",                  3],
        ] },
      { q: "When your heart and mind disagree, who usually wins?",
        o: [
          ["My mind — logic feels safer",                                   0],
          ["My heart — it's rarely wrong, even when it costs me",           1],
          ["The advice of someone I trust, more than either",               2],
          ["Neither — I stay stuck between them for too long",              3],
        ] },
      { q: "How do you know a decision is actually the right one?",
        o: [
          ["Every angle checks out and I've slept on it more than once",    0],
          ["My body eases the moment I say it out loud",                    1],
          ["The people I trust nod when I explain it",                      2],
          ["I stop having to think about it, one way or the other",         3],
        ] },
      { q: "What have you learned about how you make big decisions?",
        o: [
          ["I over-think — but my thinking usually protects me",            0],
          ["I trust myself faster than I used to",                          1],
          ["I decide better with the right person beside me",               2],
          ["I still hesitate a beat too long — and life moves anyway",      3],
        ] },
    ],
    emotional_clarity: [
      { q: "When facing a major decision, what troubles you the most?",
        o: [
          ["Being unable to name what I actually feel about it",            0],
          ["Feeling like I'm being pushed toward it by others",             1],
          ["Feeling nothing at all — just the weight",                      2],
          ["The tension of knowing I need to act soon",                     3],
        ] },
      { q: "When a decision doesn't sit right after you make it, what happens?",
        o: [
          ["I sit with the discomfort until it tells me what it is",        0],
          ["I look for someone to blame — the circumstances, the pressure", 1],
          ["I go quiet and stop mentioning it",                             2],
          ["I make a small correction the same day",                        3],
        ] },
      { q: "What's the feeling that dominates as you approach a big decision?",
        o: [
          ["An honest heaviness I can actually name",                       0],
          ["Frustration at how much this shouldn't be on me",               1],
          ["A hollow silence — I stop thinking about it",                   2],
          ["A steady readiness — I'm going in, prepared",                   3],
        ] },
      { q: "How do you feel after making a hard choice?",
        o: [
          ["Softer — I've let the truth settle in",                         0],
          ["Sharp — some part of me is still fighting the outcome",         1],
          ["Numb — I stop discussing it with anyone",                       2],
          ["Focused — I move immediately into the next step",               3],
        ] },
    ],
    risk_readiness: [
      { q: "How comfortable are you with irreversible decisions?",
        o: [
          ["Not at all — I want doors I can walk back through",             0],
          ["Fully — the decisions that matter usually have no return",      1],
          ["I avoid them — I need reversibility to breathe",                2],
          ["When the timing is right, I move without needing an exit",      3],
        ] },
      { q: "When the stakes are highest, what wins in you?",
        o: [
          ["Protection — I default to the safer path",                      0],
          ["Growth — I lean into what could stretch me",                    1],
          ["Freeze — I can't move at all",                                  2],
          ["Timing — I know when to move and when to wait",                 3],
        ] },
      { q: "How do you relate to a choice that could remake your life?",
        o: [
          ["I want to keep what I've built more than I want the remake",    0],
          ["I've been waiting for it — I'd move quickly",                   1],
          ["I'd stall — the unknown after is unbearable",                   2],
          ["I'd say yes when the moment is fully ripe",                     3],
        ] },
      { q: "What role does risk play in the decisions you're most proud of?",
        o: [
          ["Very little — safety helped me build slowly",                   0],
          ["Almost everything — I risked what I had to grow",               1],
          ["Barely — the risky ones still scare me in memory",              2],
          ["Right timing — I risked exactly when I needed to",              3],
        ] },
    ],
    purpose_alignment: [
      { q: "What kind of answer are you actually looking for right now?",
        o: [
          ["Reassurance — that whatever I choose, I'll be okay",            0],
          ["Recognition — that this matters, that I matter",                1],
          ["Permission — that I'm allowed to want what I want",             2],
          ["Courage — because I already know what to do",                   3],
        ] },
      { q: "Underneath this decision, what are you really choosing between?",
        o: [
          ["Safety and change",                                             0],
          ["Being seen and being ignored",                                  1],
          ["Freedom and loyalty",                                           2],
          ["The person I've been and the person I could become",            3],
        ] },
      { q: "What outcome would let you exhale?",
        o: [
          ["A stable, calm, uneventful future",                             0],
          ["Being appreciated for the choice I made",                       1],
          ["The freedom to choose again if I want to",                      2],
          ["A life that clearly matters — to me and to others",             3],
        ] },
      { q: "Which decision from your past changed your life the most?",
        o: [
          ["A choice that protected something important to me",             0],
          ["A choice people finally noticed me for",                        1],
          ["A choice that gave me my life back",                            2],
          ["A choice that started meaning I still live inside",             3],
        ] },
    ],
    self_trust: [
      { q: "When you replay a past decision, what usually stings?",
        o: [
          ["That I could have known better if I'd looked harder",           0],
          ["That people saw me stumble",                                    1],
          ["That the situation ran me instead of me running it",            2],
          ["That I let a bigger version of my life slip past",              3],
        ] },
      { q: "What's the fear that hides underneath this decision?",
        o: [
          ["That I'll pick the wrong door and can't undo it",               0],
          ["That people will see me differently after",                     1],
          ["That the ground under my life will move",                       2],
          ["That I'll never fully become who I could be",                   3],
        ] },
      { q: "What holds you back from choosing what you already know is true?",
        o: [
          ["I might still be wrong — the certainty scares me",              0],
          ["Others will judge the choice",                                  1],
          ["It would change too much, too fast",                            2],
          ["If I choose this and still fail, there's nothing bigger",       3],
        ] },
      { q: "What do you actually need to feel before you decide?",
        o: [
          ["That I've thought about it enough to defend it",                0],
          ["That I've been quietly witnessed in this",                      1],
          ["That I can still steer if the ground shifts",                   2],
          ["That the person I'm becoming approves of it",                   3],
        ] },
    ],
  },
};
